import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Control,
  Controller,
  FieldErrors,
  useFormContext,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, AlertCircle, Clock, Calendar } from "lucide-react";
import { Event } from "@/types/event";
import { format, addDays, isWithinInterval } from "date-fns";
import { useGetFacilitiesQuery } from "@/services/faciltiesApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

type DaysState = {
  [key: string]: {
    is_available: boolean;
    time_slots: { start_time: string; end_time: string };
  };
};

interface EventAvailabilityFormProps {
  control: Control<Event>;
  errors: FieldErrors<Event>;
  watch: UseFormWatch<Event>;
  setValue: UseFormSetValue<Event>;
  trigger?: UseFormTrigger<Event>;
  action: 'add' | 'edit';
  open: boolean;
}

const EventAvailabilityForm = ({ control, errors, watch, setValue, trigger, action, open }: EventAvailabilityFormProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { setValue: formSetValue, getValues } = useFormContext();

  // Watch for start_date, end_date changes
  const startDate = watch("start_date") as string | undefined;
  const endDate = watch("end_date") as string | undefined;
  const facilityName = watch("facility_name");

  // Fetch facilities for mock data since APIs aren't ready
  const { data: facilitiesData, isLoading: isFacilitiesLoading } =
    useGetFacilitiesQuery({ orgId, query:"" });

  const dayNames = {
    Monday: "Monday",
    Tuesday: "Tuesday",
    Wednesday: "Wednesday",
    Thursday: "Thursday",
    Friday: "Friday",
    Saturday: "Saturday",
    Sunday: "Sunday",
  };

  const initialDaysState: DaysState = {
    Monday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Tuesday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Wednesday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Thursday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Friday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Saturday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Sunday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
  };

  const [dayErrors, setDayErrors] = useState<{
    [key: string]: { start_time?: string; end_time?: string };
  }>({});

  // Get existing days data from form or use initial state
  const existingDays = getValues("availability");
  const [days, setDays] = useState<DaysState>(() => {
    if (existingDays) {
      // Convert form data structure (lowercase keys) to component state (capitalized keys)
      return {
        Monday: existingDays.monday || initialDaysState.Monday,
        Tuesday: existingDays.tuesday || initialDaysState.Tuesday,
        Wednesday: existingDays.wednesday || initialDaysState.Wednesday,
        Thursday: existingDays.thursday || initialDaysState.Thursday,
        Friday: existingDays.friday || initialDaysState.Friday,
        Saturday: existingDays.saturday || initialDaysState.Saturday,
        Sunday: existingDays.sunday || initialDaysState.Sunday,
      };
    }
    return initialDaysState;
  });

  // Watch availability from form to sync state when navigating back
  const formAvailability = watch("availability");
  
  // Use a ref to track if we're updating from internal state to prevent circular updates
  const isInternalUpdate = useRef(false);

  // Sync days state with form data when form data changes (e.g., when navigating back)
  // Only sync if the change is external (not from our own updates)
  useEffect(() => {
    if (isInternalUpdate.current) {
      // Reset the flag - this was our update, don't sync back
      isInternalUpdate.current = false;
      return;
    }

    if (formAvailability) {
      const newDaysState = {
        Monday: formAvailability.monday || initialDaysState.Monday,
        Tuesday: formAvailability.tuesday || initialDaysState.Tuesday,
        Wednesday: formAvailability.wednesday || initialDaysState.Wednesday,
        Thursday: formAvailability.thursday || initialDaysState.Thursday,
        Friday: formAvailability.friday || initialDaysState.Friday,
        Saturday: formAvailability.saturday || initialDaysState.Saturday,
        Sunday: formAvailability.sunday || initialDaysState.Sunday,
      };

      // Update state - React will handle the comparison internally
      setDays((prevDays) => {
        // Only update if the state is actually different to prevent unnecessary re-renders
        const prevDaysString = JSON.stringify(prevDays);
        const newDaysString = JSON.stringify(newDaysState);
        
        if (prevDaysString === newDaysString) {
          return prevDays; // Return same reference if unchanged
        }
        
        return newDaysState;
      });
    }
  }, [formAvailability, open]);

  // Mock facility available days and operating hours
  const [facilityAvailableDays, setFacilityAvailableDays] = useState<string[]>([]);
  const [facilityOperatingHours, setFacilityOperatingHours] = useState<{
    [key: string]: { start_time: string; end_time: string };
  }>({});

  // Mock data for facilities since APIs aren't ready
  useEffect(() => {
    if (facilitiesData?.data && facilityName) {
      // Find the facility by name for mock data
      const facility = facilitiesData.data.find(f => f.name?.toLowerCase().includes(facilityName.toLowerCase()));

      if (facility) {
        // Mock available days - in real implementation this would come from facility.availability
        const mockAvailableDays: string[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        const mockOperatingHours: {
          [key: string]: { start_time: string; end_time: string };
        } = {
          Monday: { start_time: "09:00", end_time: "18:00" },
          Tuesday: { start_time: "09:00", end_time: "18:00" },
          Wednesday: { start_time: "09:00", end_time: "18:00" },
          Thursday: { start_time: "09:00", end_time: "18:00" },
          Friday: { start_time: "09:00", end_time: "18:00" },
        };

        setFacilityAvailableDays(mockAvailableDays);
        setFacilityOperatingHours(mockOperatingHours);
      }
    }
  }, [facilitiesData, facilityName]);

  // Function to get day name from date
  const getDayName = (date: Date): keyof typeof dayNames => {
    const dayIndex = date.getDay();
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return daysOfWeek[dayIndex] as keyof typeof dayNames;
  };

  // Determine which days to display based on start and end dates
  const getVisibleDays = (): string[] => {
    if (!startDate) {
      return Object.keys(dayNames);
    }

    const startDateObj = new Date(startDate);
    const endDateObj = endDate ? new Date(endDate) : null;

    if (!endDate) {
      return [getDayName(startDateObj)];
    }

    if (startDate === endDate) {
      return [getDayName(startDateObj)];
    }

    if (endDateObj) {
      const daysInRange = new Set<string>();
      daysInRange.add(getDayName(startDateObj));

      let currentDate = startDateObj;
      while (
        format(currentDate, "yyyy-MM-dd") !== format(endDateObj, "yyyy-MM-dd")
      ) {
        currentDate = addDays(currentDate, 1);
        daysInRange.add(getDayName(currentDate));
      }

      return Array.from(daysInRange);
    }

    return Object.keys(dayNames);
  };

  const visibleDays = getVisibleDays();

  // Get the single slot time for a given day
  const getSlotTime = (day: string): string | null => {
    const dayConfig = days[day];
    if (
      !dayConfig.is_available ||
      !dayConfig.time_slots.start_time ||
      !dayConfig.time_slots.end_time
    ) {
      return null;
    }

    const startTime = dayConfig.time_slots.start_time;
    const endTime = dayConfig.time_slots.end_time;
    
    // Return the single slot time range for this day
    return `${startTime} - ${endTime}`;
  };

  useEffect(() => {
    // Update form values whenever days change
    // Mark this as an internal update to prevent circular sync
    isInternalUpdate.current = true;
    
    formSetValue(
      "availability",
      {
        monday: days.Monday,
        tuesday: days.Tuesday,
        wednesday: days.Wednesday,
        thursday: days.Thursday,
        friday: days.Friday,
        saturday: days.Saturday,
        sunday: days.Sunday,
      },
      { shouldDirty: true, shouldTouch: true }
    );
  }, [days, formSetValue]);

  const toggleDay = (day: string) => {
    setDays((prev) => {
      const newState = {
        ...prev,
        [day]: {
          ...prev[day],
          is_available: !prev[day].is_available,
        },
      };

      // If toggling to available, set default times from facility
      if (!prev[day].is_available) {
        const facilityHours = facilityOperatingHours[day];

        if (facilityHours) {
          newState[day].time_slots = {
            start_time: facilityHours.start_time,
            end_time: facilityHours.end_time,
          };
        } else {
          newState[day].time_slots = {
            start_time: "09:00",
            end_time: "17:00",
          };
        }
      }

      return newState;
    });

    // Clear errors when toggling day
    setDayErrors((prev) => ({
      ...prev,
      [day]: {},
    }));
  };

  const updateTime = (
    day: string,
    field: "start_time" | "end_time",
    value: string
  ) => {
    const facilityHours = facilityOperatingHours[day];

    if (!facilityHours) {
      setDayErrors((prev) => ({
        ...prev,
        [day]: {
          ...prev[day],
          [field]: "Facility operating hours not available",
        },
      }));
      return;
    }

    setDays((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        time_slots: {
          ...prev[day].time_slots,
          [field]: value,
        },
      },
    }));

    const updatedDayConfig = {
      ...days[day],
      time_slots: {
        ...days[day].time_slots,
        [field]: value,
      },
    };

    const startTime = updatedDayConfig.time_slots.start_time;
    const endTime = updatedDayConfig.time_slots.end_time;

    // Clear previous errors for this day
    setDayErrors((prev) => ({
      ...prev,
      [day]: {},
    }));

    // Validate start time
    if (startTime) {
      if (startTime < facilityHours.start_time) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            start_time: `Start time must be at least ${facilityHours.start_time}`,
          },
        }));
      } else if (startTime >= facilityHours.end_time) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            start_time: `Start time must be before ${facilityHours.end_time}`,
          },
        }));
      } else if (endTime && startTime >= endTime) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            start_time: "Start time must be before end time",
          },
        }));
      }
    }

    // Validate end time
    if (endTime) {
      if (endTime > facilityHours.end_time) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            end_time: `End time must be at most ${facilityHours.end_time}`,
          },
        }));
      } else if (endTime <= facilityHours.start_time) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            end_time: `End time must be after ${facilityHours.start_time}`,
          },
        }));
      } else if (startTime && endTime <= startTime) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: {
            ...prev[day],
            end_time: "End time must be after start time",
          },
        }));
      }
    }
  };

  const clearDayTimes = (day: string) => {
    setDays((prev) => ({
      ...prev,
      [day]: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
    }));

    setDayErrors((prev) => ({
      ...prev,
      [day]: {},
    }));
  };

  // Helper function to format time for display
  const formatTimeRange = (startTime: string, endTime: string): string => {
    return `${startTime} - ${endTime}`;
  };

  // Check if at least one day is selected
  const hasSelectedDays = Object.values(days).some(day => day.is_available);

  return (
    <div className="space-y-4" data-component="event-availability">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-base font-medium">
            Event Availability
            {startDate && endDate && (
              <span className="ml-2 text-sm text-gray-500 font-normal">
                ({format(new Date(startDate), "dd MMM yyyy")} to{" "}
                {format(new Date(endDate), "dd MMM yyyy")})
              </span>
            )}
          </CardTitle>
          <Separator className="h-[1px]" />
        </CardHeader>
        <CardContent className="space-y-6">
          {!facilityName && (
            <div className="text-amber-600 flex items-center text-sm">
              <AlertCircle className="h-5 w-5 mr-1" />
              Please select a facility first to configure availability
            </div>
          )}

          {!startDate && facilityName && (
            <div className="text-amber-600 flex items-center text-sm">
              <AlertCircle className="h-5 w-5 mr-1" />
              Please select Start and End dates first
            </div>
          )}

          {startDate && endDate && facilityName && visibleDays.length === 0 ? (
            <div className="text-red-600 flex items-center text-sm">
              <AlertCircle className="h-5 w-5 mr-1" />
              Selected date range does not cover any available facility days. Please adjust your date range.
            </div>
          ) : !hasSelectedDays && startDate && visibleDays.length > 0 ? (
            <div className="text-red-600 flex items-center text-sm">
              <AlertCircle className="h-5 w-5 mr-1" />
              Please select at least one day to proceed
            </div>
          ) : null}

          {startDate && visibleDays.length > 0 && (
            <>
              <div className="grid grid-cols-4 gap-4 items-start w-[100%]">
                <div className="text-sm font-medium text-zinc-500">Days</div>
                <div className="text-sm font-medium text-zinc-500">
                  Slot Start Time
                </div>
                <div className="text-sm font-medium text-zinc-500">
                  Slot End Time
                </div>
                <div className="text-sm font-medium text-zinc-500">Actions</div>
              </div>

              {Object.entries(days).map(([day, value]) => {
                if (!visibleDays.includes(day)) {
                  return null;
                }

                const facilityHours = facilityOperatingHours[day];

                return (
                  <div key={day}>
                    <div data-day={day} className="grid grid-cols-4 gap-4 items-start w-[100%]">
                      <div>
                        <button
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={cn(
                            "h-10 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center w-full",
                            value.is_available
                              ? "bg-primary text-white"
                              : "bg-white border border-input text-zinc-600 hover:bg-zinc-100"
                          )}
                        >
                          {day}
                        </button>
                      </div>

                      <div className="flex flex-col ">
                        <Input
                          placeholder="Select Slot Start Time"
                          value={value.time_slots.start_time}
                          onChange={(e) =>
                            updateTime(day, "start_time", e.target.value)
                          }
                          type="time"
                          step="60"
                          disabled={!value.is_available}
                          className={cn(
                            "border-input focus-within:ring-0 h-10",
                            !value.is_available && "opacity-50",
                            dayErrors[day]?.start_time && "border-red-500"
                          )}
                          title={facilityHours ? `Available range: ${facilityHours.start_time} - ${facilityHours.end_time}` : undefined}
                        />
                        {dayErrors[day]?.start_time && (
                          <span className="text-xs text-red-500 mt-1">
                            {dayErrors[day]?.start_time}
                          </span>
                        )}
                        {facilityHours && value.is_available && (
                          <span className="text-xs text-gray-500 mt-1">
                            Facility hours: {formatTimeRange(facilityHours.start_time, facilityHours.end_time)}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <Input
                          placeholder="Select Slot End Time"
                          value={value.time_slots.end_time}
                          onChange={(e) =>
                            updateTime(day, "end_time", e.target.value)
                          }
                          type="time"
                          step="60"
                          disabled={!value.is_available}
                          className={cn(
                            "border-input focus-within:ring-0 h-10",
                            !value.is_available && "opacity-50",
                            dayErrors[day]?.end_time && "border-red-500"
                          )}
                          title={facilityHours ? `Available range: ${facilityHours.start_time} - ${facilityHours.end_time}` : undefined}
                        />
                        {dayErrors[day]?.end_time && (
                          <span className="text-xs text-red-500 mt-1">
                            {dayErrors[day]?.end_time}
                          </span>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => clearDayTimes(day)}
                        disabled={!value.is_available}
                        className={cn(
                          "h-10 w-10 p-0 text-destructive",
                          !value.is_available && "opacity-50 cursor-not-allowed"
                        )}
                        title="Clear times"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                  
                  </div>
                );
              })}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventAvailabilityForm;
